import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../services/api";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Cloud,
  Loader2,
  Music,
  Save,
  Upload,
} from "lucide-react";

const GENRE_OPTIONS = [
  { value: "", label: "Selecione um genero" },
  { value: "phonk", label: "Phonk" },
  { value: "trap", label: "Trap" },
  { value: "cinematic", label: "Cinematic" },
  { value: "funk", label: "Funk" },
  { value: "pop", label: "Pop" },
  { value: "electronic", label: "Electronic" },
  { value: "other", label: "Outro" },
];

const MOOD_OPTIONS = [
  { value: "", label: "Selecione um mood" },
  { value: "energetic", label: "Energetico" },
  { value: "emotional", label: "Emocional" },
  { value: "cinematic", label: "Cinematico" },
  { value: "dark", label: "Dark" },
  { value: "uplifting", label: "Inspirador" },
  { value: "calm", label: "Calmo" },
];

const USAGE_TYPE_OPTIONS = [
  { value: "", label: "Selecione um uso" },
  { value: "viral", label: "Viral" },
  { value: "storytelling", label: "Storytelling" },
  { value: "transition", label: "Transicao" },
  { value: "motivation", label: "Motivacional" },
  { value: "general", label: "Geral" },
];

const ENERGY_OPTIONS = [
  { value: "", label: "Selecione a energia" },
  { value: "low", label: "Baixa" },
  { value: "medium", label: "Media" },
  { value: "high", label: "Alta" },
];

const buildDefaultForm = () => ({
  title: "",
  tagsText: "",
  artist: "",
  genre: "",
  mood: "",
  usageType: "",
  energyLevel: "",
});

const AdminAddSound = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [storageProvider, setStorageProvider] = useState("r2");
  const [form, setForm] = useState(buildDefaultForm);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState(null);

  const parseTags = (text) => text.split(",").map((tag) => tag.trim()).filter(Boolean);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const buildBasePayload = (fileName) => {
    const title = files.length === 1 && form.title.trim()
      ? form.title.trim()
      : fileName.replace(/\.[^/.]+$/, "");

    return {
      title,
      collection: "Músicas Virais",
      tags: parseTags(form.tagsText),
      original_filename: fileName,
      artist: form.artist.trim() || null,
      genre: form.genre || null,
      mood: form.mood || null,
      usage_type: form.usageType || null,
      energy_level: form.energyLevel || null,
    };
  };

  const handleSubmit = async () => {
    if (files.length === 0) {
      setError("Selecione pelo menos um arquivo.");
      return;
    }

    setError(null);
    setUploading(true);
    setProgress({ current: 0, total: files.length });

    try {
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const basePayload = buildBasePayload(file.name);
        setProgress({ current: i + 1, total: files.length });

        let soundPayload;
        if (storageProvider === "r2") {
          const uploaded = await api.uploadAudioR2(file);
          soundPayload = {
            ...basePayload,
            audio_url: uploaded.audio_url,
            audio_public_id: "",
            storage_provider: "r2",
            storage_key: uploaded.audio_key,
            storage_account: "D",
            duration: uploaded.duration || 0,
            format: file.name.split(".").pop() || "mp3",
          };
        } else {
          const uploaded = await api.uploadAudio(file);
          soundPayload = {
            ...basePayload,
            audio_url: uploaded.audio_url,
            audio_public_id: uploaded.audio_public_id,
            storage_provider: "cloudinary",
            storage_key: "",
            storage_account: "D",
            duration: uploaded.duration || 0,
            format: uploaded.format || "mp3",
          };
        }

        await api.createSound(soundPayload);
      }

      setUploading(false);
      navigate("/admin/sounds");
    } catch (e) {
      setUploading(false);
      setError(e.message || "Erro durante o upload. Verifique a conexao.");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] relative">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[25%] w-[520px] h-[520px] bg-[#FFCC00]/[0.03] rounded-full blur-[120px]" />
      </div>

      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-white/[0.02] border-b border-white/[0.05]">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Link
                to="/admin/sounds"
                className="w-10 h-10 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.05] flex items-center justify-center transition-all"
              >
                <ArrowLeft className="w-5 h-5 text-white/50" />
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#FFCC00]/10 border border-[#FFCC00]/20 flex items-center justify-center">
                  <Music className="w-5 h-5 text-[#FFCC00]" />
                </div>
                <div>
                  <h1 className="text-white font-medium tracking-tight">Nova Musica</h1>
                  <p className="text-white/30 text-xs">
                    {storageProvider === "r2" ? "Cloudflare R2" : "Cloudinary Conta D"}
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={handleSubmit}
              disabled={uploading}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#FFCC00]/10 hover:bg-[#FFCC00]/15 border border-[#FFCC00]/20 text-[#FFCC00] text-sm transition-all disabled:opacity-40"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span>{uploading ? `Enviando ${progress.current}/${progress.total}...` : "Salvar Tudo"}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="relative z-10 max-w-3xl mx-auto px-6 py-8 space-y-6">
        {error && (
          <div className="flex items-center gap-3 p-4 backdrop-blur-xl bg-red-500/10 rounded-2xl border border-red-500/20">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <span className="text-red-300 text-sm">{error}</span>
          </div>
        )}

        <div className="backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-6">
          <label className="block text-white/60 text-sm mb-3">Destino do arquivo</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setStorageProvider("r2")}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border font-bold text-sm transition-all ${
                storageProvider === "r2"
                  ? "bg-orange-500/20 border-orange-500 text-orange-400"
                  : "bg-white/[0.03] border-white/[0.06] text-white/40 hover:text-white/70"
              }`}
            >
              <Cloud className="w-5 h-5" />
              <span>CLOUDFLARE R2</span>
              <span className="text-[10px] font-normal opacity-70">RECOMENDADO</span>
              {storageProvider === "r2" && <CheckCircle2 className="w-4 h-4 text-orange-400" />}
            </button>

            <button
              type="button"
              onClick={() => setStorageProvider("cloudinary")}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border font-bold text-sm transition-all ${
                storageProvider === "cloudinary"
                  ? "bg-blue-500/20 border-blue-500 text-blue-400"
                  : "bg-white/[0.03] border-white/[0.06] text-white/40 hover:text-white/70"
              }`}
            >
              <Music className="w-5 h-5" />
              <span>CLOUDINARY D</span>
              <span className="text-[10px] font-normal opacity-70">LEGADO</span>
              {storageProvider === "cloudinary" && <CheckCircle2 className="w-4 h-4 text-blue-400" />}
            </button>
          </div>
        </div>

        {files.length <= 1 && (
          <div className="backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-6">
            <label className="block text-white/60 text-sm mb-3">Titulo</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => updateField("title", e.target.value)}
              className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#FFCC00]/30 focus:bg-white/[0.05] transition-all"
              placeholder="Ex: Viral Beat 01 (vazio = nome do arquivo)"
            />
          </div>
        )}

        <div className="backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-6">
          <label className="block text-white/60 text-sm mb-3">Tags (separadas por virgula)</label>
          <input
            type="text"
            value={form.tagsText}
            onChange={(e) => updateField("tagsText", e.target.value)}
            className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#FFCC00]/30 focus:bg-white/[0.05] transition-all"
            placeholder="Ex: viral, trend, instagram"
          />
        </div>

        <div className="backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-6 space-y-5">
          <div>
            <h2 className="text-white font-medium">Metadados da musica</h2>
            <p className="text-white/30 text-xs mt-1">
              Esses dados alimentam os filtros da plataforma. O sistema agora tenta sugerir o que estiver vazio.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label>
              <span className="block text-white/60 text-sm mb-3">Artista</span>
              <input
                type="text"
                value={form.artist}
                onChange={(e) => updateField("artist", e.target.value)}
                className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#FFCC00]/30 focus:bg-white/[0.05] transition-all"
                placeholder="Ex: Night Drive"
              />
            </label>

            <label>
              <span className="block text-white/60 text-sm mb-3">Genero</span>
              <select
                value={form.genre}
                onChange={(e) => updateField("genre", e.target.value)}
                className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#FFCC00]/30 focus:bg-white/[0.05] transition-all"
              >
                {GENRE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value} className="bg-[#050505]">
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span className="block text-white/60 text-sm mb-3">Mood</span>
              <select
                value={form.mood}
                onChange={(e) => updateField("mood", e.target.value)}
                className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#FFCC00]/30 focus:bg-white/[0.05] transition-all"
              >
                {MOOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value} className="bg-[#050505]">
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span className="block text-white/60 text-sm mb-3">Tipo de uso</span>
              <select
                value={form.usageType}
                onChange={(e) => updateField("usageType", e.target.value)}
                className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#FFCC00]/30 focus:bg-white/[0.05] transition-all"
              >
                {USAGE_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value} className="bg-[#050505]">
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span className="block text-white/60 text-sm mb-3">Nivel de energia</span>
              <select
                value={form.energyLevel}
                onChange={(e) => updateField("energyLevel", e.target.value)}
                className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#FFCC00]/30 focus:bg-white/[0.05] transition-all"
              >
                {ENERGY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value} className="bg-[#050505]">
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-6">
          <label className="block text-white/60 text-sm mb-3">Arquivos de audio</label>
          <div className="flex items-center gap-3">
            <label className="flex-1 cursor-pointer flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] text-white/70 transition-all">
              <Upload className="w-4 h-4" />
              <span>{files.length > 0 ? `${files.length} audio(s) selecionado(s)` : "Selecionar arquivos"}</span>
              <input
                type="file"
                accept="audio/*"
                multiple
                className="hidden"
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
              />
            </label>

            {files.length > 0 && (
              <button
                onClick={() => setFiles([])}
                className="px-4 py-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] text-white/70 transition-all"
              >
                Limpar
              </button>
            )}
          </div>

          {files.length > 0 && (
            <ul className="mt-3 space-y-1">
              {files.map((file, index) => (
                <li key={`${file.name}-${index}`} className="text-white/40 text-xs truncate">
                  {file.name}
                </li>
              ))}
            </ul>
          )}
        </div>

        {uploading && progress.total > 1 && (
          <div className="backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-4">
            <div className="flex justify-between text-white/50 text-xs mb-2">
              <span>Progresso</span>
              <span>{progress.current}/{progress.total}</span>
            </div>
            <div className="w-full bg-white/10 rounded-full h-1.5">
              <div
                className="bg-[#FFCC00] h-1.5 rounded-full transition-all"
                style={{ width: `${(progress.current / progress.total) * 100}%` }}
              />
            </div>
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={uploading}
          className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#FFCC00]/10 hover:bg-[#FFCC00]/15 border border-[#FFCC00]/25 text-[#FFCC00] font-medium transition-all disabled:opacity-40"
        >
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          <span>{uploading ? `Enviando ${progress.current} de ${progress.total}...` : "Salvar Tudo"}</span>
        </button>
      </div>
    </div>
  );
};

export default AdminAddSound;
